import { useState, useEffect, useRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjs from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchHandbookList, fetchHandbookPdfBytes, fetchHandbookThumbnailUrl } from 'services/thirteenWeeks';
import type { HandbookDoc } from 'services/thirteenWeeks';
import FullscreenViewer from 'components/FullscreenViewer';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const FALLBACK_COVER = '/logoweb.webp';

const Page = React.forwardRef<HTMLDivElement, { src: string; alt: string }>(
  ({ src, alt }, ref) => (
    <div ref={ref} style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#fff' }}>
      <img src={src} alt={alt} draggable={false} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'fill' }} />
    </div>
  )
);
Page.displayName = 'Page';

function ThirteenWeeksFlipbook({ pages, onFlip, current, total }: {
  pages: string[];
  onFlip: (index: number) => void;
  current: number;
  total: number;
}) {
  const bookRef = useRef<any>(null);
  const sceneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const parent = scene.querySelector<HTMLElement>('.stf__parent');
    if (parent) parent.style.minWidth = '0';
  });

  const flipPrev = () => bookRef.current?.pageFlip()?.flipPrev();
  const flipNext = () => bookRef.current?.pageFlip()?.flipNext();

  return (
    <div className="thirteen-weeks__hb-scene" ref={sceneRef}>
      {/* @ts-ignore */}
      <HTMLFlipBook
        ref={bookRef}
        width={550}
        height={778}
        size="stretch"
        minWidth={360}
        maxWidth={1400}
        minHeight={150}
        maxHeight={2000}
        usePortrait={true}
        showCover={false}
        mobileScrollSupport={false}
        onFlip={(e: any) => onFlip(e.data)}
        className="thirteen-weeks__hb-flipbook"
      >
        {pages.map((src, i) => (
          <Page key={i} src={src} alt={`p${i + 1}`} />
        ))}
      </HTMLFlipBook>

      <div className="thirteen-weeks__hb-nav">
        <button
          className="thirteen-weeks__hb-nav-btn"
          onClick={flipPrev}
          disabled={current <= 0}
          aria-label="Trang trước"
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 3L5 8l5 5" />
          </svg>
          <span>Trở lại</span>
        </button>
        <button
          className="thirteen-weeks__hb-nav-btn"
          onClick={flipNext}
          disabled={current >= total - 1}
          aria-label="Trang sau"
        >
          <span>Qua trang</span>
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 3l5 5-5 5" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function HandbookViewer({ doc, onBack }: { doc: HandbookDoc; onBack: () => void }) {
  const { t } = useTranslation();
  const [pages, setPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPages([]);
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const bytes = await fetchHandbookPdfBytes(doc.id);
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        const imgs: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const pg = await pdf.getPage(i);
          const vp = pg.getViewport({ scale: 2 });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width;
          canvas.height = vp.height;
          await pg.render({ canvasContext: canvas.getContext('2d')!, viewport: vp, canvas }).promise;
          imgs.push(canvas.toDataURL());
        }
        if (!cancelled) setPages(imgs);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Lỗi tải tài liệu');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id]);

  const total = pages.length;
  const displayL = current + 1;
  const displayR = Math.min(current + 2, total);

  const pageInfo = !loading && !error ? (
    <span className="thirteen-weeks__hb-page-info">
      {displayL}–{displayR} / {total}
    </span>
  ) : null;

  return (
    <div className="thirteen-weeks__hb">
      <div className="thirteen-weeks__hb-header">
        <button className="thirteen-weeks__hb-back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 3L5 8l5 5" />
          </svg>
          Quay lại
        </button>
        <div className="thirteen-weeks__hb-header-right">
          {pageInfo}
          {!loading && !error && (
            <div className="thirteen-weeks__hb-fullscreen">
              <span className="thirteen-weeks__hb-fullscreen-label">Toàn màn hình</span>
              <button
                className="thirteen-weeks__hb-fullscreen-btn"
                onClick={() => setIsFullscreen(true)}
                title="Mở toàn màn hình"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 5V1h4M11 1h4v4M15 11v4h-4M5 15H1v-4" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="thirteen-weeks__hb-loading">
          <div className="thirteen-weeks__hb-spinner" />
          <span>{t('handbookLoading')}</span>
        </div>
      ) : error ? (
        <div className="thirteen-weeks__hb-error">
          <span>{t('handbookError')}</span>
        </div>
      ) : (
        <ThirteenWeeksFlipbook pages={pages} onFlip={setCurrent} current={current} total={total} />
      )}

      <FullscreenViewer
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        aspectRatio={(550 * 2) / 778}
        headerLeft={pageInfo}
      >
        <ThirteenWeeksFlipbook pages={pages} onFlip={setCurrent} current={current} total={total} />
      </FullscreenViewer>
    </div>
  );
}

function HandbookCover({ doc }: { doc: HandbookDoc }) {
  const [src, setSrc] = useState<string | null>(doc.thumbnail_path ? null : FALLBACK_COVER);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setSrc(doc.thumbnail_path ? null : FALLBACK_COVER);
    if (doc.thumbnail_path) {
      fetchHandbookThumbnailUrl(doc.id).then(url => {
        if (cancelled) return;
        objectUrl = url;
        setSrc(url ?? FALLBACK_COVER);
      }).catch(() => {
        if (!cancelled) setSrc(FALLBACK_COVER);
      });
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id, doc.thumbnail_path]);

  if (src === null) {
    return <div className="hb-library__cover-skeleton" />;
  }

  return (
    <img
      src={src}
      alt={doc.name}
      onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_COVER; }}
    />
  );
}

function HandbookLibrary({ onOpen }: { onOpen: (item: HandbookDoc) => void }) {
  const [items, setItems] = useState<HandbookDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchHandbookList();
        if (!cancelled) setItems(list);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Lỗi tải danh sách');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="thirteen-weeks__hb-loading">
        <div className="thirteen-weeks__hb-spinner" />
        <span>Đang tải thư viện...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="thirteen-weeks__hb-error">
        <span>Không thể tải danh sách tài liệu</span>
      </div>
    );
  }

  return (
    <div className="hb-library">
      <div className="hb-library__header">
        <h2 className="hb-library__title">Cẩm nang sử dụng</h2>
        <span className="hb-library__count">{items.length} tài liệu</span>
      </div>
      <div className="hb-library__grid">
        {items.map((item) => (
          <button key={item.id} className="hb-library__card" onClick={() => onOpen(item)}>
            <div className="hb-library__cover">
              <HandbookCover doc={item} />
              <div className="hb-library__cover-overlay">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
                <span>Mở sách</span>
              </div>
              {/* <div className="hb-library__ribbon">
                <img src="/images/logo/logo-text.png" alt="" />
              </div> */}
            </div>
            <div className="hb-library__info">
              <div className="hb-library__name">{item.name}</div>
              {item.created_at && (
                <div className="hb-library__date">
                  {new Date(item.created_at).toLocaleDateString('vi-VN')}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ScreenHandbook() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDoc, setActiveDoc] = useState<HandbookDoc | null>(null);
  const [resolving, setResolving] = useState(true);

  const params = new URLSearchParams(location.search);
  const docIdParam = params.get('handbook_id');

  useEffect(() => {
    if (!docIdParam) {
      setActiveDoc(null);
      setResolving(false);
      return;
    }
    const id = parseInt(docIdParam, 10);
    fetchHandbookList().then(list => {
      const found = list.find(d => d.id === id);
      setActiveDoc(found ?? { id, name: '', file_type: 'pdf', thumbnail_path: null, created_at: '' });
    }).catch(() => {
      setActiveDoc({ id, name: '', file_type: 'pdf', thumbnail_path: null, created_at: '' });
    }).finally(() => setResolving(false));
  }, [docIdParam]);

  const openBook = (item: HandbookDoc) => {
    const p = new URLSearchParams(location.search);
    p.set('handbook_id', String(item.id));
    navigate(`${location.pathname}?${p.toString()}`, { replace: false });
    setActiveDoc(item);
  };

  const goBack = () => {
    const p = new URLSearchParams(location.search);
    p.delete('handbook_id');
    navigate(`${location.pathname}?${p.toString()}`, { replace: false });
    setActiveDoc(null);
  };

  if (resolving) {
    return (
      <div className="thirteen-weeks__hb-loading">
        <div className="thirteen-weeks__hb-spinner" />
      </div>
    );
  }

  if (activeDoc !== null) {
    return <HandbookViewer doc={activeDoc} onBack={goBack} />;
  }

  return <HandbookLibrary onOpen={openBook} />;
}
